import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppSelector } from '../../hooks';
import { IComponentData } from '../../models/requests/InspectionRequests';
import { IComponent, IDefects } from '../../models/response/InspectionResponse';
import {
  useLazyFetchComponentsQuery,
  usePatchComponentDataMutation,
  usePatchDefectMutation,
  useRemoveCarImageMutation,
  useRemoveDefectMutation,
} from '../../services/modules/inspection';
import { EmptyStateSvg } from '../../svg';
import { Button, Footer, Header, Input, LoadingLottie, Spinner, TakeImage } from '../../ui';
import { Modal } from '../../ui/Modal';
import { fileDocumentType, mediaType } from '../../ui/TakeImage';
import * as S from '../CarDetail/CarDetailElements';
import { ErrorBodyWrapper, ErrorButtons } from '../CarSegments/CarSegmentsElements';

import { Card } from './Components/Card';
import { ButtonIcon, Container, Content, StatusBar, StatusLabel } from './InspectionDetailElements';

export const InspectionDetail = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { currentCar } = useAppSelector((state) => state.inspection);
  const [components, setComponents] = useState<IComponent[]>([]);
  const [selectedItem, setSelectedItem] = useState<IComponent | null>(null);
  const [defectSlug, setDefectSlug] = useState('');
  const [imageUUID, setImageUUID] = useState('');
  const [isOpenTakeImage, onToggleIsOpenTakeImage] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const toggleShowConfirmModal = () => setShowConfirmModal((state) => !state);

  const [
    fetchComponents,
    { data: dataComponents, isLoading: isLoadingComponents, isSuccess: isSuccessComponents },
  ] = useLazyFetchComponentsQuery();

  useEffect(() => {
    const { inspection_slug, segment_slug } = params;
    if (inspection_slug && segment_slug) {
      fetchComponents({ inspection_slug, segment_slug });
    }
  }, []);

  useEffect(() => {
    if (isSuccessComponents && dataComponents) {
      setComponents(dataComponents);
    }
  }, [isSuccessComponents, dataComponents]);

  const [
    patchDefect,
    { data: dataPatchDefect, isLoading: isLoadingPatchDefect, isSuccess: isSuccessPatchDefect },
  ] = usePatchDefectMutation();

  const onChangeValues = (
    file?: File,
    note?: string,
    component_slug = selectedItem?.slug,
    dSlug?: string,
  ) => {
    const slug = params.inspection_slug;
    if (slug && component_slug) {
      const body = new FormData();
      body.append('component_slug', component_slug);
      body.append('defect_slug', dSlug ? dSlug : defectSlug);
      if (file) body.append('file', file, file.name);
      if (note) body.append('note', note);
      patchDefect({ slug, body });
    }
  };

  const update_components_images = (uuid?: string) => {
    let updatedComponents: IComponent[] = [];
    const last = [...components];
    last.map((v) => {
      if (v.slug === selectedItem?.slug) {
        const defects: IDefects[] = [];
        v.defects.map((d) => {
          if (d.defect.slug === defectSlug) {
            let images = [];
            if (d.images && uuid) {
              images = d.images?.filter((i) => i.uuid !== uuid);
            } else {
              if (d.images && d.images.length > 0) {
                images.push(...d.images);
              }
              const uploaded_file = dataPatchDefect?.uploaded_file;
              if (uploaded_file && uploaded_file.url) {
                images.push(uploaded_file);
              }
            }
            defects.push({ ...d, images, has_defect: true });
          } else {
            defects.push(d);
          }
        });
        updatedComponents.push({ ...v, defects });
      } else {
        updatedComponents.push(v);
      }
    });
    setComponents(updatedComponents);
  };

  useEffect(() => {
    if (isSuccessPatchDefect && dataPatchDefect) {
      update_components_images();
    }
  }, [isSuccessPatchDefect, dataPatchDefect]);

  const [
    removeCarImage,
    { data: dataRemoveImage, isLoading: isLoadingRemoveImage, isSuccess: isSuccessRemoveImage },
  ] = useRemoveCarImageMutation();

  const onClickRemove = () => {
    const file_uuid = imageUUID;
    const slug = params.inspection_slug;
    if (file_uuid && slug) {
      removeCarImage({ slug, file_uuid });
    }
  };

  useEffect(() => {
    if (isSuccessRemoveImage && dataRemoveImage) {
      update_components_images(dataRemoveImage.uuid);
      setShowConfirmModal(false);
    }
  }, [isSuccessRemoveImage, dataRemoveImage]);

  const [removeDefect, { isLoading: isLoadingRemoveDefect, isSuccess: isSuccessRemoveDefect }] =
    useRemoveDefectMutation();

  const onRemoveDefect = (component_slug: string, defect_slug: string) => {
    const slug = params.inspection_slug;
    const body = {
      component_slug,
      defect_slug,
    };
    if (slug) {
      removeDefect({ slug, body });
    }
  };

  const update_components_defect = () => {
    let updatedComponents: IComponent[] = [];
    components.map((v) => {
      if (v.slug === selectedItem?.slug) {
        const defects: IDefects[] = [];
        v.defects.map((d) => {
          if (d.defect.slug === defectSlug) {
            defects.push({ ...d, note: '', images: [], has_defect: !d.has_defect });
          } else {
            defects.push(d);
          }
        });
        updatedComponents.push({ ...v, defects });
      } else {
        updatedComponents.push(v);
      }
    });
    setComponents(updatedComponents);
  };

  useEffect(() => {
    if (isSuccessRemoveDefect) {
      update_components_defect();
    }
  }, [isSuccessRemoveDefect]);

  const handleUploadImage = () => {
    onToggleIsOpenTakeImage(true);
    setTimeout(() => {
      onToggleIsOpenTakeImage(false);
    }, 0);
  };

  const [patchComponentData] = usePatchComponentDataMutation();

  const onChangeOption = async (
    component_slug: string,
    available: boolean,
    description?: string,
  ) => {
    const slug = params.inspection_slug;
    if (slug) {
      const body: IComponentData = {
        component_slug: component_slug,
        available,
      };
      if (description) {
        body.description = description;
      }
      const res = await patchComponentData({ slug, body });
      if (res) {
        const data = components.map((v) => {
          if (v.slug === component_slug) {
            const value = { ...v, available };
            if (description) {
              value.description = description;
            }
            return value;
          } else {
            return v;
          }
        });
        setComponents(data);
      }
    }
  };

  const onChangeSearch = (value: string) => {
    if (dataComponents) {
      const filtered = dataComponents.filter((v) =>
        v.name.toLowerCase().includes(value.toLowerCase()),
      );
      setComponents(filtered);
    }
  };

  return (
    <Container>
      <Header
        title={
          currentCar
            ? currentCar?.model_full_name.make +
              ' ' +
              currentCar?.model_full_name.model +
              ' ' +
              currentCar?.model_full_name.year
            : ''
        }
        hasBack={true}
      />
      <Content>
        <StatusBar>
          <StatusLabel>{params.title}</StatusLabel>
          {currentCar && (
            <S.StatusLabel>{`${t('inspection.car_number')}: ${
              currentCar?.car_number
            }`}</S.StatusLabel>
          )}
        </StatusBar>
        <Input
          label={t('inspection.search_here')}
          placeholder={t('inspection.component_name')}
          marginWrapper={'0 12px 0 12px'}
          onChange={(e) => onChangeSearch(e.currentTarget.value)}
          onClearSearch={() => onChangeSearch('')}
        />
        {!isLoadingComponents && components.length === 0 && (
          <div className={'tw-flex tw-flex-col tw-items-center'}>
            <EmptyStateSvg />
            <p className={'tw-text-gray0 tw-font-bold tw-text-xl'}>
              {t('inspection.empty_state_message')}
            </p>
          </div>
        )}
        {isLoadingComponents ? (
          <LoadingLottie />
        ) : (
          components.map((item) => (
            <Card
              key={item.slug}
              {...item}
              isLoadingRemoveDefect={isLoadingRemoveDefect}
              defectSlug={defectSlug}
              onClickCheckBox={(defItem) => {
                setSelectedItem(item);
                setDefectSlug(defItem.defect.slug);
                if (defItem.has_defect) {
                  onRemoveDefect(item.slug, defItem.defect.slug);
                } else {
                  onChangeValues(undefined, undefined, item.slug, defItem.defect.slug);
                }
              }}
              onFocusInput={(defItem) => {
                setSelectedItem(item);
                setDefectSlug(defItem.defect.slug);
              }}
              onBlurInput={(value) => {
                console.log('====> value <====');

                onChangeValues(undefined, value);
              }}
              onChooseImage={onChangeValues}
              isLoadingPatchDefect={isLoadingPatchDefect}
              onClickAddImage={(defItem) => {
                handleUploadImage();
                setSelectedItem(item);
                setDefectSlug(defItem.defect.slug);
              }}
              isLoadingRemoveImage={isLoadingRemoveImage}
              imageUUID={imageUUID}
              onClickRemoveImage={(uuid, isForceDelete) => {
                setImageUUID(uuid);
                if (isForceDelete) {
                  onClickRemove();
                } else {
                  toggleShowConfirmModal();
                }
              }}
              onChangeOption={onChangeOption}
            />
          ))
        )}
      </Content>
      <Footer slug={params.slug}>
        <Button
          onClick={() => navigate(-1)}
          label={`${t('footer.finish')} ${params.title}`}
          size={'large'}
        >
          <ButtonIcon className={'icon-Right-Icon'} />
        </Button>
      </Footer>
      <TakeImage
        onChooseImage={onChangeValues}
        isRotate
        isEditImage
        allowedTypes={mediaType}
        isOpenTakeImage={isOpenTakeImage}
      />
      {isLoadingPatchDefect && (
        <div
          className={
            'tw-absolute tw-z-30 tw-bg-black tw-bg-opacity-60 tw-flex tw-items-center tw-justify-center tw-left-0 tw-right-0 tw-bottom-0 tw-top-0'
          }
        >
          <Spinner isLoading={true} />
        </div>
      )}
      <Modal
        title={t('inspection.remove_image')}
        onCloseModal={toggleShowConfirmModal}
        isShow={showConfirmModal}
      >
        <ErrorBodyWrapper>
          <p>{t('inspection.are_you_sure_you_want_remove')}</p>
        </ErrorBodyWrapper>
        <ErrorButtons>
          <Button fullWidth onClick={onClickRemove} isLoading={isLoadingRemoveImage}>
            {t('car_inspection.confirm')}
          </Button>
          <Button fullWidth color="red" onClick={toggleShowConfirmModal}>
            {t('car_inspection.cancel')}
          </Button>
        </ErrorButtons>
      </Modal>
    </Container>
  );
};
